import * as React from 'react';
import { InputHTMLAttributes, ReactNode } from 'react';
import { BaseFieldProps, Field, GenericField, WrappedFieldProps } from 'redux-form';
import { FieldWrapper, FieldWrapperConfig } from './FieldWrapper';
import InputMask from 'react-input-mask';
import classNames from 'classnames';

/**
 * Input props
 */
export interface InputComponentProps extends InputHTMLAttributes<HTMLInputElement> {
    inputStyle?: AnyObject | string;
    disabled?: boolean;
    mask: string;
    inputErrorStyle?: AnyObject | string;
    wrapperConfig?: FieldWrapperConfig;
    children?: ReactNode;
}

type InputProps = InputComponentProps & WrappedFieldProps;

/**
 * InputComponent which renders wrapped input field with label and error
 *
 * @constructor
 */
const InputComponent: React.FunctionComponent<InputProps> = (props: InputProps) => {
    const formattedProps: { [key: string]: any } = {
        disabled: false,
        name: props.input.name,
        placeholder: props.placeholder,
        value: props.input.value,
        onChange: props.input.onChange,
        maxLength: 200,
        max: props.max,
        id: props.id,
        min: props.min,
        inputStyle: props.inputStyle,
        inputErrorStyle: props.inputErrorStyle,
    };
    const InputMaskProps: any = { ...formattedProps };
    if (typeof InputMaskProps.disabled !== 'undefined') {
        delete InputMaskProps.disabled;
    }
    const hasError = !props.meta.valid && props.meta.touched;

    return (
        <FieldWrapper
            {...props.wrapperConfig}
            classes={props.wrapperConfig?.classes || {}}
            name={props.input.name}
            meta={props.meta}
        >
            <InputMask mask={props.mask} value={props.input.value} onChange={props.input.onChange}>
                {(props: any) => {
                    const mergedProps = { ...InputMaskProps, ...props };
                    return (
                        <input
                            id={mergedProps.id || undefined}
                            {...mergedProps.input}
                            className={classNames(mergedProps.inputStyle, hasError && mergedProps.inputErrorStyle)}
                            placeholder={mergedProps.placeholder}
                            type={mergedProps.type}
                            min={mergedProps.min}
                            max={mergedProps.max}
                            step={mergedProps.step}
                            accept={mergedProps.accept}
                            value={mergedProps.value}
                        />
                    );
                }}
            </InputMask>
            {props.children}
        </FieldWrapper>
    );
};

InputComponent.defaultProps = {
    inputStyle: 'form-control',
    inputErrorStyle: 'border-danger',
};

const FieldInputComponent = Field as new () => GenericField<InputComponentProps>;

type FieldProps = BaseFieldProps<InputComponentProps> & InputComponentProps;

/**
 * FieldInput component which extends GenericField from redux form and renders input field
 * (InputComponent) with all needed props
 *
 * @param props
 * @constructor
 */
export const FieldInputMask: React.FunctionComponent<FieldProps> = (props: FieldProps) => (
    <FieldInputComponent {...props} component={InputComponent} />
);
